/* eslint-disable no-undef */

/**
 * Bot Detection Utilities
 *
 * This module provides utilities to detect automated traffic (bots, crawlers,
 * headless browsers) to prevent them from polluting analytics data.
 *
 * Detection Methods:
 * 1. User Agent Analysis - checks for common bot signatures
 * 2. WebDriver Detection - identifies automated browsers (Selenium, Puppeteer)
 * 3. Document Visibility - ensures the page is actually visible to a user
 */

// Type definitions for automation tools in window object
interface AutomationWindow extends Window {
  __nightmare?: unknown;
  _phantom?: unknown;
  callPhantom?: unknown;
  __selenium_unwrapped?: unknown;
  __webdriver_unwrapped?: unknown;
  __driver_evaluate?: unknown;
  __webdriver_evaluate?: unknown;
  __selenium_evaluate?: unknown;
  __fxdriver_evaluate?: unknown;
  __driver_unwrapped?: unknown;
  __fxdriver_unwrapped?: unknown;
  chrome?: {
    runtime?: unknown;
  };
}

/**
 * List of common bot/crawler keywords found in user agent strings
 */
const BOT_KEYWORDS = [
  'bot',
  'crawl',
  'spider',
  'headlesschrome',
  'phantomjs',
  'slimerjs',
  'slurp',
  'googlebot',
  'bingbot',
  'yandex',
  'baiduspider',
  'facebookexternalhit',
  'twitterbot',
  'rogerbot',
  'linkedinbot',
  'embedly',
  'quora link preview',
  'showyoubot',
  'outbrain',
  'pinterest',
  'slackbot',
  'vkshare',
  'w3c_validator',
  'redditbot',
  'applebot',
  'whatsapp',
  'flipboard',
  'tumblr',
  'bitlybot',
  'skypeuripreview',
  'nuzzel',
  'discordbot',
  'qwantify',
  'pinterestbot',
  'bitrix link preview',
  'xing-contenttabreceiver',
  'chrome-lighthouse',
  'telegrambot',
];

/**
 * Detects if the current visitor is likely a bot/crawler
 *
 * @returns {boolean} true if the visitor appears to be a bot, false otherwise
 *
 * @example
 * ```typescript
 * if (isLikelyBot()) {
 *   console.log('Bot detected, skipping analytics');
 *   return;
 * }
 * // Safe to track analytics
 * ```
 */
export function isLikelyBot(): boolean {
  try {
    // Check 1: User Agent Analysis
    const userAgent = navigator?.userAgent?.toLowerCase() || '';
    const isBotUserAgent = BOT_KEYWORDS.some((keyword) => userAgent.includes(keyword));

    if (isBotUserAgent) {
      console.log('[Bot Detection] Bot detected via user agent:', userAgent.substring(0, 100));
      return true;
    }

    // Check 2: WebDriver Detection (Selenium, Puppeteer, etc.)
    // navigator.webdriver is set to true when automated tools control the browser
    if (navigator.webdriver) {
      console.log('[Bot Detection] Bot detected via navigator.webdriver');
      return true;
    }

    // Check 3: Document Visibility
    // Hidden tabs or background processes won't have a 'visible' state
    // This helps filter out prerender bots and background automation
    if (document.visibilityState && document.visibilityState !== 'visible') {
      console.log('[Bot Detection] Document not visible, skipping analytics');
      return true;
    }

    // Check 4: Missing essential browser features that real browsers have
    // Some headless browsers don't implement these properly
    if (typeof navigator.languages === 'undefined') {
      console.log('[Bot Detection] Missing navigator.languages');
      return true;
    }

    // Check 5: Chrome Headless specific detection
    // Headless Chrome has specific properties that can be detected
    if (userAgent.includes('headless')) {
      console.log('[Bot Detection] Headless browser detected in user agent');
      return true;
    }

    // Check 6: Plugins array is often empty in headless browsers
    // Real browsers typically have at least some plugins
    if (navigator.plugins && navigator.plugins.length === 0 && !userAgent.includes('mobile')) {
      // Note: Mobile browsers legitimately have 0 plugins, so we exclude them
      console.log('[Bot Detection] Suspicious: No plugins detected on desktop browser');
      // Don't return true here as some privacy-focused browsers block plugins
    }

    return false;
  } catch (error) {
    // If bot detection fails, assume it's NOT a bot to avoid blocking legitimate users
    console.error('[Bot Detection] Error during bot detection:', error);
    return false;
  }
}

/**
 * Enhanced bot detection with additional session validation
 * Can be used for critical events like user identity tracking
 *
 * ⚠️ WARNING: This strict mode may cause false positives!
 * The chrome.runtime check can block legitimate users with privacy extensions
 * or certain browser configurations. Use with caution or prefer isLikelyBot().
 *
 * @returns {boolean} true if the visitor appears to be a bot, false otherwise
 */
export function isLikelyBotStrict(): boolean {
  // Use base bot detection
  if (isLikelyBot()) {
    return true;
  }

  try {
    // Additional strict checks for critical events

    // Check for automation tools in window object
    const automationWindow = window as AutomationWindow;
    if (
      automationWindow.__nightmare ||
      automationWindow._phantom ||
      automationWindow.callPhantom ||
      automationWindow.__selenium_unwrapped ||
      automationWindow.__webdriver_unwrapped ||
      automationWindow.__driver_evaluate ||
      automationWindow.__webdriver_evaluate ||
      automationWindow.__selenium_evaluate ||
      automationWindow.__fxdriver_evaluate ||
      automationWindow.__driver_unwrapped ||
      automationWindow.__fxdriver_unwrapped
    ) {
      console.log('[Bot Detection - Strict] Automation tool detected in window object');
      return true;
    }

    // Check for Puppeteer/Playwright specific properties
    if (automationWindow.chrome && !automationWindow.chrome.runtime) {
      // Headless Chrome doesn't have chrome.runtime
      console.log('[Bot Detection - Strict] Missing chrome.runtime (headless indicator)');
      return true;
    }

    return false;
  } catch (error) {
    console.error('[Bot Detection - Strict] Error during strict bot detection:', error);
    return false;
  }
}

/**
 * Wrapper function to safely execute analytics functions only if user is not a bot
 *
 * @param analyticsFunction - The analytics function to execute
 * @param useStrict - Whether to use strict bot detection (default: false)
 *
 * @example
 * ```typescript
 * executeIfNotBot(() => {
 *   trackGTMEvent('page-view', { page: 'home' });
 * });
 * ```
 */
export function executeIfNotBot(analyticsFunction: () => void, useStrict = false): void {
  const isBot = useStrict ? isLikelyBotStrict() : isLikelyBot();

  if (!isBot) {
    analyticsFunction();
  }
}
